import React, { useState } from 'react'
import { CAROUSEL_TYPE, TEXT_TYPE, CARD_TYPE, ELEMENT_TYPES } from './constants'
import TextMsgForm from './forms/TextMsgForm'
import RichCardMsgForm from './forms/RichCardMsgForm'
import CarouselMsgForm from './forms/CarouselMsgForm'
import TypeButton from './TypeButton'
import { RBM_ELEMENT_ERRORS, RBM_ELEMENT_ERRORS_MESSAGES } from './constants'
import {
  BoltIcon,
  EnvelopeIcon,
  CalendarIcon,
  LinkIcon
} from '@heroicons/react/24/outline'

const getTypeForm = (
  type,
  info,
  setInfo,
  isConversational,
  replyOptions,
  errors,
  emailFields
) => {
  let MsgForm = null
  switch (type) {
    case TEXT_TYPE:
      MsgForm = TextMsgForm
      break
    case CAROUSEL_TYPE:
      MsgForm = CarouselMsgForm
      break
    case CARD_TYPE:
      MsgForm = RichCardMsgForm
      break
    default:
      MsgForm = TextMsgForm
  }
  return (
    <MsgForm
      elementInfo={info}
      setInfo={setInfo}
      isConversational={isConversational}
      replyOptions={replyOptions}
      errors={errors}
      emailFields={emailFields}
    />
  )
}

/**
 * Componente para configurar la validación de respuesta (expectedFlow)
 * Permite definir qué respuesta se espera y a dónde ir si es correcta/incorrecta
 */
const ExpectedFlowConfig = ({
  elementName,
  replyOptions,
  expectedFlowConfig,
  onChange
}) => {
  const [enabled, setEnabled] = useState(!!expectedFlowConfig)
  const [config, setConfig] = useState(
    expectedFlowConfig || {
      expectedAnswers: [''],
      elementOk: '',
      elementWrong: '',
      flowOk: [],
      flowWrong: []
    }
  )

  const updateConfig = (newConfig) => {
    setConfig(newConfig)
    onChange(newConfig)
  }

  const addExpectedAnswer = () => {
    updateConfig({
      ...config,
      expectedAnswers: [...config.expectedAnswers, '']
    })
  }

  const removeExpectedAnswer = (index) => {
    const newAnswers = config.expectedAnswers.filter((_, i) => i !== index)
    updateConfig({ ...config, expectedAnswers: newAnswers })
  }

  const updateExpectedAnswer = (index, value) => {
    const newAnswers = [...config.expectedAnswers]
    newAnswers[index] = value
    updateConfig({ ...config, expectedAnswers: newAnswers })
  }

  const getElementOptions = () => {
    // replyOptions viene del flujo actual y contiene los elementos disponibles
    return replyOptions || []
  }

  if (!enabled) {
    return (
      <div className="mb-4">
        <button
          onClick={() => {
            setEnabled(true)
            onChange(config)
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 transition-colors text-sm font-medium border border-amber-200 w-full justify-center"
        >
          <BoltIcon className="h-4 w-4" />
          Añadir validación de respuesta (expectedFlow)
        </button>
        <p className="text-xs text-gray-400 mt-2 text-center">
          Configura qué respuesta esperas y a dónde va el usuario si acierta o falla
        </p>
      </div>
    )
  }

  return (
    <div className="mb-6 p-5 bg-amber-50/70 rounded-xl border border-amber-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BoltIcon className="h-4 w-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-amber-800">Validación de Respuesta</h3>
        </div>
        <button
          onClick={() => {
            setEnabled(false)
            onChange(null)
          }}
          className="text-xs text-amber-600 hover:text-amber-800 px-2 py-1 rounded-md hover:bg-amber-100 transition-colors font-medium"
        >
          Eliminar validación
        </button>
      </div>

      {/* Respuestas esperadas */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
          Respuestas esperadas
        </label>
        <p className="text-xs text-amber-600 mb-3">
          Usa {'{variable}'} para variables de contacto (ej: {'{dniLetter}'}). Usa .* para cualquier
          respuesta.
        </p>
        <div className="space-y-2">
          {config.expectedAnswers.map((answer, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={answer}
                onChange={(e) => updateExpectedAnswer(index, e.target.value)}
                placeholder="Ej: {dniLetter} o .*"
                className="flex-1 px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              />
              {config.expectedAnswers.length > 1 && (
                <button
                  onClick={() => removeExpectedAnswer(index)}
                  className="px-2.5 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={addExpectedAnswer}
          className="mt-2 text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-amber-100 transition-colors font-medium"
        >
          <span>+</span> Añadir otra respuesta
        </button>
      </div>

      {/* Destino si acierta */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
          Si la respuesta es CORRECTA → ir a:
        </label>
        <select
          value={config.elementOk}
          onChange={(e) => updateConfig({ ...config, elementOk: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
        >
          <option value="">-- Selecciona elemento --</option>
          {getElementOptions().map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Destino si falla */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">
          Si la respuesta es INCORRECTA → ir a:
        </label>
        <select
          value={config.elementWrong}
          onChange={(e) => updateConfig({ ...config, elementWrong: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
        >
          <option value="">-- Selecciona elemento --</option>
          {getElementOptions().map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Info */}
      <div className="text-xs text-amber-700 bg-amber-100/70 p-3 rounded-lg border border-amber-200">
        <p className="font-semibold mb-1">Ejemplo para DNI:</p>
        <p>• Respuesta esperada: {'{dniLetter}'}</p>
        <p>• Si acierta → checkReason-2/2</p>
        <p>• Si falla → dniSecondTry-1/1</p>
      </div>
    </div>
  )
}

const NewElementForm = ({
  elementInfo: info = {},
  elementIndex = null,
  onClick,
  setOpen,
  isConversational = false,
  errors,
  replyOptions = [],
  emailFields = [],
  expectedFlow = {},
  onExpectedFlowChange,
  elementKey = ''
}) => {
  const [elementInfo, setElementInfo] = useState(info)
  const [type, setType] = useState(info.type || TEXT_TYPE)
  const [name, setName] = useState(info.name || '')

  // Obtener la configuración de expectedFlow para este elemento
  const getExpectedFlowForElement = () => {
    if (!elementKey || !expectedFlow) return null
    // elementKey viene como "init-1/1", necesitamos el nombre del flujo
    const flowName = elementKey.split('-')[0]
    return expectedFlow[flowName] || expectedFlow[elementKey] || null
  }

  const handleExpectedFlowChange = (config) => {
    if (onExpectedFlowChange && elementKey) {
      onExpectedFlowChange(elementKey, config)
    }
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900">
          {info.name ? 'Editar Elemento' : 'Crear nuevo elemento'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {info.name
            ? 'Modifica los datos del elemento seleccionado'
            : 'Configura un nuevo mensaje o tarjeta para el flujo'}
        </p>
      </div>

      {/* Info de acciones */}
      <div className="mb-5 p-4 bg-blue-50 rounded-xl border border-blue-100">
        <div className="flex items-center gap-2 mb-3">
          <BoltIcon className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-800">Acciones disponibles en botones</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="flex gap-2.5 items-start p-2.5 bg-white rounded-lg border border-blue-100">
            <EnvelopeIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-700">Enviar Email</p>
              <p className="text-gray-400 mt-0.5">Disparar email al hacer click</p>
            </div>
          </div>
          <div className="flex gap-2.5 items-start p-2.5 bg-white rounded-lg border border-blue-100">
            <CalendarIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-700">Programar Flujo</p>
              <p className="text-gray-400 mt-0.5">Programar flujo para mañana</p>
            </div>
          </div>
          <div className="flex gap-2.5 items-start p-2.5 bg-white rounded-lg border border-blue-100">
            <LinkIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-700">Webhook</p>
              <p className="text-gray-400 mt-0.5">Disparar webhook externo</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sección de Validación de Respuesta - SOLO para tipo texto en modo conversacional */}
      {isConversational && type === TEXT_TYPE && (
        <ExpectedFlowConfig
          elementName={name}
          replyOptions={replyOptions}
          expectedFlowConfig={getExpectedFlowForElement()}
          onChange={handleExpectedFlowChange}
        />
      )}

      <div className="space-y-4">
        <div>
          <label
            className="block text-sm font-semibold text-gray-700 mb-1.5"
            htmlFor="elementName"
          >
            Nombre del Elemento
          </label>
          <input
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-colors"
            type="text"
            id="elementName"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Ej: Mensaje de bienvenida"
          />
          {errors.includes(RBM_ELEMENT_ERRORS.EMPTY_NAME) && (
            <p className="text-red-500 text-xs mt-1.5">
              {RBM_ELEMENT_ERRORS_MESSAGES[RBM_ELEMENT_ERRORS.EMPTY_NAME]}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de elemento</label>
          <div className="flex gap-2 flex-wrap">
            {ELEMENT_TYPES.map((elementType) => (
              <TypeButton
                key={elementType.type}
                type={elementType.type}
                selected={type === elementType.type}
                onClick={setType}
                icon={elementType.icon}
              >
                {elementType.name}
              </TypeButton>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 pt-5 border-t border-gray-100">
        {getTypeForm(
          type,
          elementInfo,
          setElementInfo,
          isConversational,
          replyOptions,
          errors,
          emailFields
        )}
      </div>

      <div className="w-full flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
        <button
          onClick={() => setOpen(false)}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={() => onClick({ ...elementInfo, type, name }, elementIndex)}
          className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
        >
          {info.name ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </div>
  )
}

export default NewElementForm
